import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/config';

const PROFILES = [
  {
    name: 'The Pioneer',
    description: 'You consider a destination ruined the moment it appears on a top ten list. You want the place before the crowds find it.',
  },
  {
    name: 'The Adventurer',
    description: 'You want to come home having seen something most people have never even googled. Discovery is the point.',
  },
  {
    name: 'The Explorer',
    description: 'One anchor to orient yourself, everything else is discovery. You use the Eiffel Tower as a landmark, not a destination.',
  },
  {
    name: 'The Curious Traveler',
    description: 'Half familiar, half discovery. You come home with stories and recommendations nobody else at the table has heard.',
  },
  {
    name: 'The Selective Explorer',
    description: 'You appreciate the classics but always look for that one place nobody else at the dinner table has been to.',
  },
  {
    name: 'The Classics',
    description: 'You know what you love and you love it for good reason. The iconic destinations deliver and you are here for every bit of it.',
  },
];

const SAMPLE_DESTINATIONS = [
  { name: 'Kyoto', country: 'Japan', tier: 'iconic' },
  { name: 'Tbilisi', country: 'Georgia', tier: 'underrated' },
  { name: 'Kotor', country: 'Montenegro', tier: 'hidden_gem' },
];
const TIER_COLORS = {
  iconic: '#7eb8c9',
  underrated: '#b8a0e8',
  hidden_gem: '#7ec49b',
};

const TIER_BG = {
  iconic: '#2a6b8a',
  underrated: '#3d2f6b',
  hidden_gem: '#1a5c3a',
};

const TIER_LABELS = {
  iconic: 'Iconic',
  underrated: 'Underrated',
  hidden_gem: 'Hidden Gem',
};
const TIER_ICONS = {
  iconic: '🏛️',
  underrated: '🧭',
  hidden_gem: '💎',
};
function Landing() {
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [destinations, setDestinations] = useState(SAMPLE_DESTINATIONS);
  const [profileIndex, setProfileIndex] = useState(0);
  const [fading, setFading] = useState(false);

  function goToProfile(direction) {
    setFading(true);
    setTimeout(() => {
      setProfileIndex(prev => (prev + direction + PROFILES.length) % PROFILES.length);
      setFading(false);
    }, 400);
  }
  // Rotate traveler profiles every 4 seconds. Resets whenever profileIndex
  // changes (auto or manual click) so a manual navigation doesn't get
  // immediately overridden by a stale auto-rotation tick.
  useEffect(() => {
    const interval = setInterval(() => {
      goToProfile(1);
    }, 4000);
    return () => clearInterval(interval);
  }, [profileIndex]);

  // Fetch real destinations from the API
  useEffect(() => {
    fetch(`${API_BASE}/quiz/questions`)
      .then(() => {
        // API is reachable - fetch random destinations
        // For now we use sample data and swap in real API call in v1.0
        // when we have a dedicated random destinations endpoint
      })
      .catch(() => {
        // API not reachable - keep sample destinations
      });
  }, []);

  const currentProfile = PROFILES[profileIndex];

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Logo */}
        <div style={styles.logo}>Carpe Wonder™</div>
        <div style={styles.slogan}>Seize the Wonder</div>
        <div style={styles.tagline}>Because the world isn't going to see itself.</div>
        {/* Hero */}
        <h1 style={styles.headline}>Where should you go next?</h1>
        <p style={styles.subline}>
          Tell us who you are as a traveler. We'll find where you belong.
        </p>

        {/* Destination pills */}
        <div style={styles.pillRow}>
          {destinations.map((dest, i) => (
            <div key={i} style={{
              ...styles.pill,
              background: TIER_BG[dest.tier],
              border: `0.5px solid ${TIER_COLORS[dest.tier]}33`,
            }}>
              <div style={styles.pillName}>{dest.name}</div>
              <div style={styles.pillCountryName}>{dest.country}</div>
              <div style={{...styles.pillCountry, color: TIER_COLORS[dest.tier]}}>
                {TIER_ICONS[dest.tier]} {TIER_LABELS[dest.tier]}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <button style={styles.button} onClick={() => {
          const userId = sessionStorage.getItem('carpeUserId');
          if (userId) {
            navigate('/welcome');
          } else {
            navigate('/login');
          }
        }}>
          <div>Seize your next adventure →</div>
          <div style={styles.buttonHintInner}>Your perfect trip is closer than you think.</div>
        </button>
         
        {/* Divider */}
        <div style={styles.divider} />

        {/* Rotating profile card */}
        <div style={styles.profileCard}>
          <div style={styles.profileLabel}>Are you...</div>
          <div style={{
            ...styles.profileName,
            opacity: fading ? 0 : 1,
            transition: 'opacity 0.4s ease',
          }}>
            {currentProfile.name}?
          </div>
          <div style={{
            ...styles.profileDesc,
            opacity: fading ? 0 : 1,
            transition: 'opacity 0.4s ease',
          }}>
            {currentProfile.description}
          </div>

          {/* Manual navigation + dots */}
          <div style={styles.profileNavRow}>
            <button
              style={styles.profileArrowBtn}
              onClick={() => goToProfile(-1)}
              aria-label="Previous profile"
            >
              ‹
            </button>
            <div style={styles.dots}>
              {PROFILES.map((_, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.dot,
                    background: i === profileIndex
                      ? 'var(--gold)'
                      : 'var(--gold-dim)',
                  }}
                />
              ))}
            </div>
            <button
              style={styles.profileArrowBtn}
              onClick={() => goToProfile(1)}
              aria-label="Next profile"
            >
              ›
            </button>
          </div>
        </div>

      </div>
      <div style={{
        textAlign: 'center',
        padding: '24px 20px',
        fontSize: '11px',
        color: 'rgba(255,255,255,0.3)',
        lineHeight: '1.6',
      }}>
        <a
          href="https://www.discovercars.com/?a_aid=carpewonder"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'underline' }}
        >
          Rent a car at your destination
        </a>
        {' · '}
        Carpe Wonder may earn a commission when you book through our links. This site uses cookies and tracking for analytics and affiliate purposes.
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1f',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '0 16px',
  },
  container: {
    width: '100%',
    maxWidth: '420px',
    paddingTop: '48px',
    paddingBottom: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    fontSize: '28px',
    fontWeight: '600',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  slogan: {
    fontSize: '16px',
    color: 'rgba(201,168,76,0.85)',
    letterSpacing: '0.08em',
    marginTop: '4px',
    marginBottom: '4px',
  },
  tagline: {
    fontSize: '14px',
    color: 'rgba(201,168,76,0.75)',
    letterSpacing: '0.06em',
    fontStyle: 'italic',
    marginBottom: '40px',
  },
  headline: {
    fontSize: '28px',
    fontWeight: '500',
    color: '#fff',
    textAlign: 'center',
    lineHeight: '1.3',
    marginBottom: '12px',
  },
  subline: {
    fontSize: '15px',
    color: 'rgba(255,255,255,0.7)',
    textAlign: 'center',
    lineHeight: '1.7',
    marginBottom: '28px',
    maxWidth: '320px',
  },
  pillRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '28px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  pill: {
    borderRadius: '24px',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '120px',
    flexShrink: 0,
  },
  pillName: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#fff',
  },
  pillCountry: {
    fontSize: '12px',
    marginTop: '2px',
  },
  pillCountryName: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.55)',
    marginBottom: '4px',
  },
  button: {
    background: 'var(--gold)',
    border: '2px solid rgba(255,255,255,0.8)',
    borderRadius: '28px',
    padding: '16px 32px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#0d0d1f',
    letterSpacing: '0.02em',
    marginBottom: '28px',
    width: '100%',
    maxWidth: '320px',
    cursor: 'pointer',
    minHeight: '44px',
  },
  divider: {
    width: '100%',
    maxWidth: '320px',
    borderTop: '0.5px solid rgba(255,255,255,0.12)',
    marginBottom: '24px',
  },
  profileCard: {
    border: '0.5px solid rgba(201,168,76,0.25)',
    borderRadius: '16px',
    padding: '24px 20px',
    width: '100%',
    maxWidth: '320px',
    textAlign: 'center',
    background: 'rgba(201,168,76,0.04)',
  },
  profileLabel: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: '10px',
  },
  profileName: {
    fontSize: '22px',
    fontWeight: '500',
    color: 'var(--gold)',
    marginBottom: '10px',
  },
  profileDesc: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
    lineHeight: '1.7',
    marginBottom: '16px',
  },
  profileNavRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  profileArrowBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--gold)',
    fontSize: '22px',
    lineHeight: '1',
    padding: '4px 10px',
    cursor: 'pointer',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  buttonHintInner: {
    fontSize: '13px',
    color: 'rgba(13,13,31,0.6)',
    marginTop: '4px',
    fontWeight: '400',
    letterSpacing: '0',
  },
};
export default Landing;